// @flow
import type { Address, Country } from '../../entities';
import type { FieldValidator } from '../form';
import { FormValidator } from '../form';

export type Messages = {
  label: string;
  placeholder: string;
  error: string;
};

type CountryMessages = {
  admin1: Messages;
  admin2: Messages;
  admin3?: Messages;
  address1: Messages;
  address2: Messages;
};

export function getAddressesIntlIds(country: Country): CountryMessages {
  let base = {
    admin1: {
      label: '',
      placeholder: '',
      error: '',
    },
    admin2: {
      label: '',
      placeholder: '',
      error: '',
    },
    address1: {
      label: '',
      placeholder: '',
      error: '',
    },
    address2: {
      label: '',
      placeholder: '',
      error: '',
    }
  };
  switch (country) {
    case 'PE':
      base = {
        admin1: {
          label: 'address.admin1.department.label',
          placeholder: 'address.admin1.department.placeholder',
          error: 'address.admin1.department.error.required',
        },
        admin2: {
          label: 'address.admin2.province.label',
          placeholder: 'address.admin2.province.placeholder',
          error: 'address.admin2.province.error.required',
        },
        admin3: {
          label: 'address.admin3.district.label',
          placeholder: 'address.admin3.district.placeholder',
          error: 'address.admin3.district.error.required',
        },
        address1: {
          label: 'address.address.label',
          placeholder: 'address.address.placeholder',
          error: 'address.address.error.required',
        },
        address2: {
          label: 'address.reference.label',
          placeholder: 'address.reference.placeholder',
          error: 'address.reference.error.required',
        }
      };
      break;
    case 'US':
      base = {
        admin1: {
          label: 'address.admin1.state.label',
          placeholder: 'address.admin1.state.placeholder',
          error: 'address.admin1.state.error.required',
        },
        admin2: {
          label: 'address.admin2.city.label',
          placeholder: 'address.admin2.city.placeholder',
          error: 'address.admin2.city.error.required',
        },
        address1: {
          label: 'address.address1.label',
          placeholder: 'address.address1.placeholder',
          error: 'address.address1.error.required',
        },
        address2: {
          label: 'address.address2.label',
          placeholder: 'address.address2.placeholder',
          error: 'address.address2.error.required',
        }
      };
      break;
    case 'CO':
      base = {
        admin1: {
          label: 'address.admin1.department.label',
          placeholder: 'address.admin1.department.placeholder',
          error: 'address.admin1.department.error.required',
        },
        admin2: {
          label: 'address.admin2.municipality.label',
          placeholder: 'address.admin2.municipality.placeholder',
          error: 'address.admin2.municipality.error.required',
        },
        address1: {
          label: 'address.address.label',
          placeholder: 'address.address.placeholder',
          error: 'address.address.error.required',
        },
        address2: {
          label: 'address.reference.label',
          placeholder: 'address.reference.placeholder',
          error: 'address.reference.error.required',
        }
      };
      break;
  }
  return base;
}

export function getAddressFormValidator(country: Country, type: string, extra: FieldValidator = {}): FormValidator {
  let formValidator = new FormValidator({});
  const baseValidator = {
    admin1: { required: (value: number) => typeof value !== 'number' && !value, },
    admin2: { required: (value: number) => typeof value !== 'number' && !value, },
    address1: { required: 'required', },
  };
  switch (type) {
    case 'modal':
      const modalValidator = {
        country: { required: 'required', },
        firstName: { required: 'required', },
        lastName: { required: 'required', },
        name: { required: 'required', },
        phone: { required: 'required', },
      };
      switch (country) {
        case 'PE':
          formValidator = new FormValidator({
            ...baseValidator,
            ...modalValidator,
            admin3: { required: (value: number) => typeof value !== 'number' && !value, },
            idNumber: { required: 'required', },
            ...extra,
          });
          break;
        case 'US':
          formValidator = new FormValidator({
            ...baseValidator,
            ...modalValidator,
            //zipCode: { required: 'required', },
            ...extra,
          });
          break;
        case 'CO':
          formValidator = new FormValidator({
            ...baseValidator,
            ...modalValidator,
            idNumber: { required: 'required', },
            ...extra,
          });
          break;
      }
      break;
    case 'billing':
    case 'myAddress':
    case 'standAlone':
      const validator: any = {
        firstName: { required: 'required' },
        lastName: { required: 'required' },
        phone: { required: 'required' },
        //zipCode: { required: 'required', },
      };
      if (type === 'standAlone') {
        validator.name = { required: 'required' };
      }
      switch (country) {
        case 'PE':
          formValidator = new FormValidator({
            ...baseValidator,
            ...validator,
            admin3: { required: (value: number) => typeof value !== 'number' && !value, },
            idNumber: { required: 'required', },
            ...extra,
          });
          break;
        case 'US':
          formValidator = new FormValidator({
            ...baseValidator,
            ...validator,
            ...extra,
          });
          break;
        case 'CO':
          formValidator = new FormValidator({
            ...baseValidator,
            ...validator,
            idNumber: { required: 'required', },
            ...extra,
          });
          break;
      }
      break;
    case 'store':
      const storeValidator = {
        shopName: { required: 'required', },
      };
      switch (country) {
        case 'PE':
          formValidator = new FormValidator({
            ...baseValidator,
            ...storeValidator,
            admin3: { required: (value: number) => typeof value !== 'number' && !value, },
            ...extra,
          });
          break;
        case 'US':
          formValidator = new FormValidator({
            ...baseValidator,
            ...storeValidator,
            ...extra,
            zipCode: { required: 'required', },
          });
          break;
        case 'CO':
          formValidator = new FormValidator({
            ...baseValidator,
            ...storeValidator,
            ...extra,
          });
          break;
      }
      break;
    case 'payout':
      const payoutValidator = {
        bank: { required: (value: number): boolean => value === 0 },
        firstName: { required: 'required', },
        lastName: { required: 'required', },
        phone: { required: 'required', },
      };
      switch (country) {
        case 'PE':
          formValidator = new FormValidator({
            ...baseValidator,
            ...payoutValidator,
            accountNumber: { required: 'required', },
            accountNumberCci: { required: 'required', },
            admin3: { required: (value: number) => typeof value !== 'number' && !value, },
            idNumber: { required: 'required', },
            ...extra,
          });
          break;
        case 'US':
          formValidator = new FormValidator({
            ...baseValidator,
            ...payoutValidator,
            accountNumber: { required: 'required' },
            routingNumber: { required: 'required' },
            // zipCode: { required: 'required', },
            ...extra,
          });
          break;
        case 'CO':
          formValidator = new FormValidator({
            ...baseValidator,
            ...payoutValidator,
            accountNumber: { required: 'required' },
            idNumber: { required: 'required' },
            ...extra,
          });
          break;
      }
      break;
  }

  return formValidator;
}

export function getFullAddress(address: Address): string {
  return address.address2 ? `${address.address1}, ${address.address2}` : `${address.address1}`;
}

export function getFullAdmin(address: Address): string {
  return address.admin3 && address.admin3.name ?
    `${address.admin3.name}, ${address.admin2.name}, ${address.admin1.name}` :
    `${address.admin2.name}, ${address.admin1.name}`;
}

export function parseFromForm(form?: { [key: string]: any }): ?Address {
  if (!form) return undefined;
  const address: ?Address = {};
  Object.keys(form).forEach((key: string) => {
    if (address && form) {
      if (key === 'admin1' || key === 'admin2' || key === 'admin3') {
        address[key] = {
          id: form[key].value,
          name: form[key].name,
        };
      } else if (form[key].value) {
        address[key] = form[key].value;
      }
    }
  });
  return address;
}



// WEBPACK FOOTER //
// ./src/utils/address/index.js